import React, {useState, useRef, useEffect} from 'react';
import {Text} from '@/ui-kits';

interface TableCellTooltipProps {
  message: string;
  children: React.ReactNode;
  maxWidth?: number;
  minWidth?: number;
}

export const TableCellTooltip: React.FC<TableCellTooltipProps> = ({message, children, maxWidth = 400, minWidth = 200}) => {
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [showAbove, setShowAbove] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 👇 Close tooltip on scroll or resize
  useEffect(() => {
    if (!visible) return;

    const handleScrollOrResize = () => {
      setVisible(false);
    };

    window.addEventListener('scroll', handleScrollOrResize, true); // true = capture (handles nested scroll)
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [visible]);

  const handleMouseEnter = () => {
    setVisible(true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      // Default to showing below, but check if there's more space above
      const shouldShowAbove = spaceAbove > spaceBelow && spaceAbove > 200;
      setShowAbove(shouldShowAbove);

      // Calculate horizontal position (center on cell, but keep within viewport)
      let left = rect.left + rect.width / 2;
      const tooltipWidth = Math.min(maxWidth, viewportWidth - 40);

      // Adjust if tooltip would go off screen
      if (left + tooltipWidth / 2 > viewportWidth - 20) {
        left = viewportWidth - tooltipWidth / 2 - 20;
      }
      if (left - tooltipWidth / 2 < 20) {
        left = tooltipWidth / 2 + 20;
      }

      // Calculate tooltip position
      const tooltipOffset = 10;
      let topPosition: string;

      if (shouldShowAbove) {
        // Position above: use bottom property to position from bottom of viewport
        // We want tooltip to end just above the cell
        topPosition = `${rect.top - tooltipOffset}px`;
        // We'll use transform to position from bottom
      } else {
        // Position below: use top property
        topPosition = `${rect.bottom + tooltipOffset}px`;
      }

      setTooltipStyle({
        position: 'fixed',
        left: `${left}px`,
        top: shouldShowAbove ? 'auto' : topPosition,
        bottom: shouldShowAbove ? `${window.innerHeight - rect.top + tooltipOffset}px` : 'auto',
        transform: shouldShowAbove ? 'translate(-50%, 100%)' : 'translate(-50%, 0)',
        zIndex: 9999,
        maxWidth: `${tooltipWidth}px`,
      });
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block w-full" onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div
          className="relative w-max max-w-md min-w-[200px] px-3 py-2 rounded-md shadow-lg bg-ui_sky_blue border border-ui_blue whitespace-normal break-words"
          style={{...tooltipStyle, minWidth: `${minWidth}px`}}>
          <Text variant="small" className="text-ui_blue font-FigtreeMedium whitespace-pre-line break-words">
            {message}
          </Text>
          {/* triangle pointing to cell */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 ${
              showAbove
                ? 'top-full -mt-1 border-l-4 border-r-4 border-b-4 border-l-transparent border-b-ui_sky_blue border-r-transparent'
                : 'bottom-full -mb-1 border-l-4 border-r-4 border-t-4 border-l-transparent border-t-ui_sky_blue border-r-transparent'
            }`}
          />
        </div>
      )}
    </div>
  );
};
